import React from 'react';
import { connect } from 'react-redux';
import { validate } from 'validate.js';
import { api } from 'services/api';

import ValidationElement from 'components/Form/ValidationElement';
import Street from 'components/Form/Street';
import State from 'components/Form/State';
import MilitaryState from 'components/Form/MilitaryState';
import City from 'components/Form/City';
import Country from 'components/Form/Country';
import ZipCode from 'components/Form/ZipCode';
import Spinner, { SpinnerAction } from 'components/Form/Spinner';
import Suggestions from 'components/Form/Suggestions';

import { countryValueResolver } from 'helpers/location';

import { updateApplication } from '@redux/modules/application/actions';
import classnames from 'classnames';
import env from 'config/environment';
import { i18n } from 'config/locales';
import { updateCurrentScrollValue } from '@redux/modules/form/actions';
import { COUNTRY_POSTOFFICE } from 'constants/enums/locations';
import { ApplicableModel } from 'forms/shared/types';
import { countryString } from '../../../validators/location';
import { LocationValidator } from '../../../validators';
import Layouts from './Layouts';
import { AddressSuggestion } from './AddressSuggestion';
import ToggleableLocation from './ToggleableLocation';
import SvgComponent from '../SvgComponent';
import AddressUnknown from './AddressUnknown';

export const timeout = (fn, milliseconds = 400, w = window) => {
  if (!w) {
    return;
  }

  w.setTimeout(fn, milliseconds);
};

export class LocationUnknown extends ValidationElement {
  private static PVQ_COURT_MARTIAL_LOCATION: string;

  private static PVQ_COURT_MARTIAL_OFFENSE_LOCATION: string;

  constructor(props) {
    super(props);

    // Instance field to prevent setState calls after unmount
    this.geocodeCancel = false;

    // Animations
    this.animationDelay = null;
    this.cancelAnimations = this.cancelAnimations.bind(this);
    this.delayAnimations = this.delayAnimations.bind(this);
    this.triggerAnimations = this.triggerAnimations.bind(this);
    this.renderSuggestion = this.renderSuggestion.bind(this);
    this.handleFocus = this.handleFocus.bind(this);

    this.updateCountryFocused = this.updateCountryFocused.bind(this);

    this.state = {
      uid: `${this.props.name}-${super.guid()}`,
      geocodeResult: props.geocodeResult,
      spinner: this.props.spinner,
      spinnerState: SpinnerAction.ACTION_SPIN,
      suggestions: this.props.suggestions,
      hasFocus: false,
    };
  }

  update = (queue) => {
    const values = {
      uid: this.state.uid,
      inUSA: this.props.inUSA,
      street: this.props.street,
      streetUnknown: this.props.streetUnknown,
      street2: this.props.street2,
      city: this.props.city,
      cityUnknown: this.props.cityUnknown,
      zipcode: this.props.zipcode,
      state: this.props.state,
      stateUnknown: this.props.stateUnknown,
      county: this.props.county,
      country: this.props.country,
      countryNotApplicable: this.props.countryNotApplicable,
      countryComments: this.props.countryComments,
      countryFocused: this.props.countryFocused,
      layout: this.props.layout,
      validated: this.props.validated,
      zipcodeUnknown: this.props.zipcodeUnknown,
      apoFpoDpoPostcode: this.props.apoFpoDpoPostcode,
      apoFpoNotApplicable: this.props.apoFpoNotApplicable,
      isMilitaryDiplomaticAddress: this.props.isMilitaryDiplomaticAddress,
      militaryDiplomaticInstallationName: this.props.militaryDiplomaticInstallationName,
      militaryInstallationNameNotApplicable: this.props.militaryInstallationNameNotApplicable,
      militaryDiplomaticInstallationNameExplanation: this.props.militaryDiplomaticInstallationNameExplanation,
      ...queue,
    };

    // Clear out zipcode if changing to international
    if (queue.country) {
      const newCountry = countryString(queue.country);
      if (!['United States', 'POSTOFFICE'].includes(newCountry)) {
        values.state = '';
        values.zipcode = '';
      }
    }

    // Send the update back upstream
    this.props.onUpdate(values);

    // If there is an associated address book then push the updates there
    if (this.props.addressBook && values.validated) {
      const updatedBook = this.appendToAddressBook(
        this.props.addressBooks,
        this.props.addressBook,
        values
      );
      this.props.dispatch(updateApplication('AddressBooks', this.props.addressBook, updatedBook));
    } else {
      const updatedBook = this.removeFromAddressBook(
        this.props.addressBooks,
        this.props.addressBook,
        values
      );
      this.props.dispatch(updateApplication('AddressBooks', this.props.addressBook, updatedBook));
    }
  };

  removeFromAddressBook = (books, name, address) => (books[name] || []).filter((a) => a.uid !== address.uid);

  appendToAddressBook = (books, name, address) => {
    const addressCountry = countryString(address.country);

    let book = books[name] || [];

    // If this is a full address and domestic then it must be validate
    if (
      address.layout === Layouts.US_ADDRESS
      && addressCountry === 'United States'
      && !address.validated
    ) {
      return book;
    }

    // Make sure there are **some values** at least
    switch (addressCountry) {
      case 'United States':
      case 'POSTOFFICE':
        if (!address.street || !address.city || !address.state || !address.zipcode) {
          return book;
        }
        break;

      default:
        if (!address.street || !address.city || !addressCountry) {
          return book;
        }
        break;
    }

    // Look to see if we can just update it first.
    let updated = false;
    for (let i = 0; i < books.length; i += 1) {
      // If this address matches the same location let us just update it.
      if (books[i].uid === address.uid) {
        updated = true;
        books[i] = address // eslint-disable-line
      }
    }

    // If this address matches something that pre-exists somewhere else no need
    // to append to the address book.
    let skip = true;
    book = book.filter((a) => {
      const countryValue = (a.country || {}).value;
      if (['United States', 'POSTOFFICE'].includes(addressCountry)) {
        if (a.street.toLowerCase() === address.street.toLowerCase()
          && a.city.toLowerCase() === address.city.toLowerCase()
          && a.state.toLowerCase() === address.state.toLowerCase()
          && a.zipcode.toLowerCase() === address.zipcode.toLowerCase()) {
          updated = true;
          if (skip) {
            skip = false;
            return true;
          }
          return false;
        }
      } else if (
        a.street.toLowerCase() === address.street.toLowerCase()
        && a.city.toLowerCase() === address.city.toLowerCase()
        && countryValue.toLowerCase() === addressCountry.toLowerCase()
      ) {
        updated = true;
        if (skip) {
          skip = false;
          return true;
        }
        return false;
      }

      return true;
    });

    // If not updated the append to the list.
    if (!updated) {
      book.push(address);
    }

    return book;
  };

  updateCountryFocused(focus: boolean) {
    this.update({ countryFocused: focus });
  }

  animateCloseWithSuggestions() {
    timeout(() => {
      // There were errors/suggestions so show them
      this.setState({
        spinner: false,
        spinnerAction: SpinnerAction.Spin,
        suggestions: true,
      });
    }, 1000);
  }

  animateCloseTimeout() {
    timeout(() => {
      this.setState({
        spinner: true,
        spinnerAction: SpinnerAction.Shrink,
      });

      // Reset back for next usage
      timeout(() => {
        this.setState({
          spinner: false,
          spinnerAction: SpinnerAction.Spin,
          suggestions: true,
        });
      });
    });
  }

  animateCloseValid() {
    timeout(() => {
      this.setState({
        spinner: true,
        spinnerAction: SpinnerAction.Shrink,
      });

      timeout(() => {
        // Grow the green arrow
        this.setState({
          spinner: true,
          spinnerAction: SpinnerAction.Grow,
        });

        // Reset back for next usage
        timeout(() => {
          this.setState(
            {
              spinner: false,
              spinnerAction: SpinnerAction.Spin,
              suggestions: false,
            },
            () => {
              this.update({ validated: true });
            }
          );
        }, 1000);
      });
    }, 1000);
  }

  cancelAnimations(w = window) {
    if (this.animationDelay) {
      w.clearTimeout(this.animationDelay);
    }
  }

  delayAnimations(ms, w = window) {
    this.cancelAnimations();
    this.animationDelay = w.setTimeout(this.triggerAnimations, ms); // eslint-disable-line @typescript-eslint/unbound-method
  }

  uspsValidateLog = (originalLoc, acceptedLoc) => {
    const payload = {
      type: 'locationlog',
      props: {
        originalLoc,
        acceptedLoc
      }
    };
    api.validateLog(payload)?.catch((err) => console.error(err));
  };

  triggerAnimations() {
    // If we can't geocode or it is already validated we skip validation.
    const validator = new LocationValidator(this.props);
    if (!this.props.geocode || this.props.validated || !validator.canGeocode() || this.state.hasFocus) {
      return;
    }

    // If spinner or suggestions are active then we skip validation.
    if (this.state.spinner || this.state.suggestions) {
      return;
    }

    this.cancelAnimations();
    this.setState({ spinner: true, suggestions: false }, () => {
      validator
        .geocode()
        .then((r) => {
          if (this.geocodeCancel) {
            return;
          }

          this.setState({ geocodeResult: r });
        })
        .then(() => {
          // Trigger the spinner to complete final animations
          if (this.showSuggestions()) {
            // There were errors/suggestions so show them
            this.animateCloseWithSuggestions();
          } else {
            this.animateCloseValid();
          }
        })
        .catch(() => {
          try {
            this.animateCloseTimeout();
          } catch (err) {
            console.error(err);
          }
        });
    });
  }

  componentDidUpdate(prevProps, prevState) {
    const { showPostOffice, form } = this.props;
    const { suggestions } = this.state;
    if (
      prevProps.showPostOffice !== showPostOffice
      && showPostOffice === false
      && (prevProps.country || {}).value === 'POSTOFFICE'
    ) {
      this.update({
        country: 'United States',
        city: '',
        state: '',
        zipcode: '',
      });
    }
    if (prevState.suggestions !== suggestions && !suggestions && !this.showSuggestions()) {
      // Add scroll fix for modal close; address validation modal removes scroll setting, so need to re-add it
      if (form?.currentScrollValue) {
        window.scroll({ top: form.currentScrollValue, left: 0, behavior: 'smooth' });
        this.props.updateCurrentScrollValue(null);
      }
    }
  }

  onAddressUpdate = ({ name, value }) => {
    this.update({
      [name]: value,
    });
  };

  updateCountry = (values) => {
    this.update({
      country: { value: values.value ?? '' },
      countryComments: values.comments,
    });
  };

  updateZipcode = (values) => {
    this.update({
      zipcode: values.value,
      validated: this.props.validated && values.value === this.props.zipcode,
    });
  };

  updateAddress = (address, delay = null) => {
    if (this.props.form?.currentScrollValue) {
      this.props.updateCurrentScrollValue(null);
    }
    if (delay !== null) {
      if (delay === 0) {
        this.cancelAnimations();
      } else {
        this.delayAnimations(delay);
      }
    }

    this.update({
      validated: false,
      ...address,
    });
  };

  updateToggleableLocation = (location) => {
    this.update({
      apoFpoDpoPostcode: location.apoFpoDpoPostcode,
      city: location.city,
      isMilitaryDiplomaticAddress: location.isMilitaryDiplomaticAddress,
      militaryDiplomaticInstallationName: location.militaryDiplomaticInstallationName,
      militaryDiplomaticInstallationNameExplanation: location.militaryDiplomaticInstallationNameExplanation,
      militaryInstallationNameNotApplicable: location.militaryInstallationNameNotApplicable,
      state: location.state,
      zipcode: location.zipcode,
      county: location.county,
      country: location.country,
      countryComments: location.countryComments,
      inUSA: location.inUSA,
      validated: false,
    });
  };

  handleFocus = (event) => {
    this.setState({ hasFocus: true }, () => {
      super.handleFocus(event);
    });
  };

  handleBlur = (event) => {
    this.setState({ hasFocus: false }, () => {
      super.handleBlur(event);
    });
  };

  handleError = (value, arr) => {
    /* eslint no-param-reassign: 0 */
    arr = arr.map((err) => ({
      code: `location.${err.code}`,
      valid: err.valid,
      uid: err.uid,
    })) || [];
    /* eslint no-param-reassign: 1 */

    this.props.onError(value, arr);
    return arr;
  };

  /**
   * Determines what conditions render the address suggestion modal
   * EN-2132 always show suggestsions, even when there are system errors in order to display message to user
   */
  showSuggestions() {
    return !validate.isEmpty(this.state.geocodeResult);
  }

  suggestionTitle() {
    if (!this.state.geocodeResult.Error) {
      return i18n.t('error.geocode.system.title');
    }
    return i18n.t(`${this.state.geocodeResult.Error}.title`);
  }

  suggestionLabel() {
    const e = this.state.geocodeResult.Error;
    if (e === 'error.geocode.partial') {
      return i18n.t(`${e}.label`);
    }
    return '';
  }

  suggestionParagraph() {
    if (!this.state.geocodeResult.Error) {
      return i18n.t('error.geocode.system.para');
    }
    const e = this.state.geocodeResult.Error;
    if (e === 'error.geocode.defaultAddress') {
      return (
        <span>
          <p>{i18n.t(`${e}.para`)}</p>
          <button
            type="button"
            className="suggestion-btn"
            onClick={this.onSuggestionDismiss.bind(this, 'alternate')}
          >
            <span>{i18n.t('suggestions.address.more')}</span>
            <div className="arrow-circle-right">
              <SvgComponent className="arrow-circle-right" src="/img/arrow-circle-right.svg" />
            </div>
          </button>
        </span>
      );
    }
    return i18n.m(`${e}.para`);
  }

  dismissAlternative() {
    const e = this.state.geocodeResult.Error;
    if (e === 'error.geocode.defaultAddress') {
      return null;
    }

    if (
      !this.state.geocodeResult.Suggestions
      || this.state.geocodeResult.Suggestions.length === 0
    ) {
      return i18n.t('suggestions.address.alternate');
    }

    return null;
  }

  onSuggestionDismiss = (action) => {
    this.setState({ spinner: false, suggestions: false, geocodeResult: {} }, () => {
      this.update({
        validated: action === 'dismiss',
      });
    });
  };

  onSuggestion = (suggestion) => {
    const {
      street, street2, city, state, zipcode
    } = this.props;

    const originalLoc = {
      street, street2, city, state, zipcode
    };
    this.setState({ spinner: false, suggestions: false, geocodeResult: {} }, () => {
      const acceptedLoc = {
        street: suggestion.Street,
        street2: suggestion.Street2,
        city: suggestion.City,
        state: suggestion.State,
        zipcode: suggestion.Zipcode,
      };
      this.update({
        ...acceptedLoc,
        validated: true,
      });
      this.uspsValidateLog(originalLoc, acceptedLoc);
    });
  };

  suggestionDismissContent() {
    const {
      street, street2, city, state, zipcode
    } = this.props;

    return (
      <div>
        <h5>{i18n.t('error.geocode.original.title')}</h5>
        <div className="address-suggestion">
          <div>{street}</div>
          <div>{street2}</div>
          <div>{`${city}, ${state} ${zipcode}`}</div>
        </div>
      </div>
    );
  }

  enforceLayout(fields: Array<'street' | 'street2' | 'city' | 'zipcode' | 'state' | 'county' | 'country'>) {
    const enforcedLayout = {
      street: '',
      street2: '',
      city: '',
      zipcode: '',
      state: '',
      county: '',
      country: '',
    };

    let field: keyof typeof enforcedLayout;
    let changed = false;

    // Copy over from this.props only the props permitted from parameter provided
    for (let i = 0; i < fields.length; i++) {
      enforcedLayout[fields[i]] = this.props[fields[i]];
    }

    if (this.props.forceAPO) {
      if (this.props.country !== COUNTRY_POSTOFFICE) {
        enforcedLayout.country = COUNTRY_POSTOFFICE;
        changed = true;
      }
    }

    // Detect whether any props are needed to be erased before updating
    for (field in enforcedLayout) {
      if (!changed && enforcedLayout[field] !== this.props[field]) {
        changed = true;
      }
    }

    if (changed) {
      this.update(enforcedLayout);
    }
  }

  renderSuggestion(suggestion) {
    return <AddressSuggestion suggestion={suggestion} current={this.props} />;
  }

  renderFields = (fields) => fields.map((field, index) => {
    switch (field) {
      case 'street':
        return (
          <Street
            attribute={`${this.props.attribute}.street`}
            name="street"
            key={field + index.toString()}
            className="street"
            label={this.props.streetLabel}
            placeholder={this.props.streetPlaceholder}
            value={this.props.street}
            disabled={this.props.disabled}
            onUpdate={this.onAddressUpdate}
            onError={this.handleError}
            onFocus={this.props.onFocus}
            required={this.props.required}
          />
        );
      case 'street2':
        return (
          <Street
            attribute={`${this.props.attribute}.street2`}
            name="street2"
            key={field + index.toString()}
            className="street2"
            label={this.props.street2Label}
            optional
            value={this.props.street2}
            disabled={this.props.disabled}
            onUpdate={this.onAddressUpdate}
            onError={this.handleError}
            onFocus={this.props.onFocus}
          />
        );
      case 'city':
        return (
          <City
            attribute={`${this.props.attribute}.city`}
            name="city"
            className="city"
            key={field + index.toString()}
            label={this.props.cityLabel}
            placeholder={this.props.cityPlaceholder}
            value={this.props.city}
            disabled={this.props.disabled}
            onUpdate={this.onAddressUpdate}
            onError={this.handleError}
            onFocus={this.props.onFocus}
            required={this.props.required}
          />
        );
      case 'state':
        return (
          <State
            attribute={`${this.props.attribute}.state`}
            name="state"
            className="state"
            key={field + index.toString()}
            label={this.props.stateLabel}
            placeholder={this.props.statePlaceholder}
            value={this.props.state}
            includeStates="true"
            disabled={this.props.disabled}
            onUpdate={this.onAddressUpdate}
            onError={this.handleError}
            onBlur={this.handleBlur}
            onFocus={this.props.onFocus}
            required={this.props.required}
          />
        );
      case 'militaryState':
        return (
          <MilitaryState
            attribute={`${this.props.attribute}.militaryState`}
            name="state"
            key={field + index.toString()}
            className="state"
            label={this.props.stateLabel}
            placeholder={this.props.statePlaceholder}
            value={this.props.state}
            includeStates="true"
            disabled={this.props.disabled}
            onUpdate={this.onAddressUpdate}
            onError={this.handleError}
            onFocus={this.props.onFocus}
            onBlur={this.handleBlur}
            required={this.props.required}
          />
        );
      case 'stateZipcode':
        return (
          <div className="state-zip-wrap" key={`state-zip-${field + index.toString()}`}>
            <State
              attribute={`${this.props.attribute}.state`}
              name="state"
              className="state"
              label={this.props.stateLabel}
              placeholder={this.props.statePlaceholder}
              value={this.props.state}
              includeStates="true"
              disabled={this.props.disabled}
              onUpdate={this.onAddressUpdate}
              onError={this.handleError}
              onBlur={this.handleBlur}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
            <ZipCode
              attribute={`${this.props.attribute}.zipcode`}
              name="zipcode"
              className="zipcode"
              label={this.props.zipcodeLabel}
              placeholder={this.props.zipcodePlaceholder}
              value={this.props.zipcode}
              disabled={this.props.disabled}
              onUpdate={this.updateZipcode}
              onError={this.handleError}
              onFocus={this.props.onFocus}
              required={this.props.required}
            />
          </div>
        );
      case 'country':
        return (
          <Country
            isPartOfLocationModel
            attribute={`${this.props.attribute}.country`}
            name="country"
            {...countryValueResolver(this.props)}
            className="country"
            key={field + index.toString()}
            label={this.props.countryLabel}
            updateCountryFocused={this.updateCountryFocused}
            placeholder={this.props.countryPlaceholder}
            disabled={this.props.disabled}
            excludeUnitedStates={this.props.excludeUnitedStates}
            onUpdate={this.updateCountry}
            onError={this.handleError}
            onFocus={this.props.onFocus}
            required={this.props.required}
          />
        );
      default:
        return null;
    }
  });

  /**
   * Maps fields to Location constant to determine layout
   */
  fieldMappings() {
    switch (this.props.layout) {
      case LocationUnknown.IDENTIFICATION_BIRTH_PLACE:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['city', 'county', 'state']}
            internationalFields={['city', 'country']}
            onBlur={this.handleBlur}
            onUpdate={this.updateToggleableLocation}
            onError={this.handleError}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      case LocationUnknown.PVQ_COURT_MARTIAL_LOCATION:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['city', 'county', 'state', 'militaryDiplomaticInstallationName']}
            domesticTitle={this.props.domesticTitle}
            foreignTitle={this.props.foreignTitle}
            internationalFields={['city', 'country', 'apoFpoDpoPostcode', 'militaryDiplomaticInstallationName']}
            militaryDiplomaticInstallationNameLabel={this.props.militaryDiplomaticInstallationNameLabel}
            onBlur={this.handleBlur}
            onError={this.handleError}
            onUpdate={this.updateToggleableLocation}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
            zipcodeLabel={this.props.zipcodeLabel}
          />
        );
      case LocationUnknown.PVQ_COURT_MARTIAL_OFFENSE_LOCATION:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['city', 'county', 'stateZipcode', 'usMilitaryInstallation']}
            domesticTitle={this.props.domesticTitle}
            foreignTitle={this.props.foreignTitle}
            internationalFields={['city', 'country', 'usMilitaryInstallationDiplomaticFacility']}
            onBlur={this.handleBlur}
            onError={this.handleError}
            onUpdate={this.updateToggleableLocation}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
            zipcodeLabel={this.props.zipcodeLabel}
          />
        );
      case LocationUnknown.PVQ_DOMESTIC_FOREIGN_MILITARY_DIPLOMATIC_LOCATION:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['street', 'city', 'stateZipcode', 'usMilitaryInstallation']}
            domesticTitle={this.props.domesticTitle}
            foreignTitle={this.props.foreignTitle}
            internationalFields={['city', 'country', 'usMilitaryInstallationDiplomaticFacility']}
            onBlur={this.handleBlur}
            onError={this.handleError}
            onUpdate={this.updateToggleableLocation}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
            zipcodeLabel={this.props.zipcodeLabel}
          />
        );
      case LocationUnknown.US_STREET_CITY_STATE_ZIP:
        this.enforceLayout(['street', 'city', 'state', 'zipcode']);
        return this.renderFields(['street', 'city', 'stateZipcode']);
      case LocationUnknown.US_CITY_STATE_COUNTY_INTERNATIONAL_CITY_COUNTRY:
      case LocationUnknown.BIRTHPLACE:
        return (
          <AddressUnknown
            {...this.props}
            inUsaUnknown={this.props.inUsaUnknown}
            inUsaUnknownLabel={this.props.inUsaUnknownLabel}
            addressUnknown={this.props.addressUnknown}
            addressUnknownLabel={this.props.addressUnknownLabel}
            hideInstallationNameIfInternational={this.props.hideInstallationNameIfInternational}
            disabled={this.props.disabled}
            hideInstallationNameUnknownExplanation={this.props.hideInstallationNameUnknownExplanation}
            onFocus={this.handleFocus}
            onUpdateAddressUnknown={this.props.onUpdateAddressUnknown}
            showAddressUnknown={this.props.showAddressUnknown}
            onUpdateInUsaUnknown={this.props.onUpdateInUsaUnknown}
            showInUsaUnknown={this.props.showInUsaUnknown}
            onBlur={this.handleBlur}
            showInstallationNameUnknown={this.props.showInstallationNameUnknown}
            hideInstallationNameUnknownWhenForeign={this.props.hideInstallationNameUnknownWhenForeign}
            isEnabled={this.props.isEnabled}
            isPoBoxAllowed={this.props.isPoBoxAllowed}
            disableToggle={this.props.layout === LocationUnknown.US_ADDRESS || this.props.disableToggle}
            showMilitaryInstallation={this.props.showMilitaryInstallation}
            onUpdate={this.updateAddress}
            onError={this.handleError}
            required={this.props.required}
            useChangeWarning={this.props.useChangeWarning}
            showDisabledText={this.props.showDisabledText}
            showIsInstallationUnknownIfInternational={this.props.showIsInstallationUnknownIfInternational}
            showIsInstallationUnknownIfDomestic={this.props.showIsInstallationUnknownIfDomestic}
            showApoFpoUnknown={this.props.showApoFpoUnknown}
            showApoFpoDpoPostcodeWhenForeign={this.props.showApoFpoDpoPostcodeWhenForeign}
            showInternationalStreet={this.props.showInternationalStreet}
            showInternationalStreetUnknown={this.props.showInternationalStreetUnknown}
            internationalStreetLabel={this.props.internationalStreetLabel}
            includeMilitaryStates={this.props.includeMilitaryStates}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      // XXX This first location doesnt seem to be used in code at all
      case LocationUnknown.US_CITY_STATE_INTERNATIONAL_CITY_COUNTRY:
      case LocationUnknown.BIRTHPLACE_WITHOUT_COUNTY:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['city', 'state']}
            internationalFields={['city', 'country']}
            onBlur={this.handleBlur}
            onUpdate={this.updateToggleableLocation}
            onError={this.handleError}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      case LocationUnknown.INTERNATIONAL_CITY_COUNTRY_ONLY:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={[]}
            internationalFields={['city', 'country']}
            onBlur={this.handleBlur}
            onUpdate={this.updateToggleableLocation}
            onError={this.handleError}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      case LocationUnknown.US_COUNTY_CITY_STATE_ZIP_INTERNATIONAL_CITY_COUNTRY:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['county', 'city', 'stateZipcode']}
            internationalFields={['city', 'country']}
            onBlur={this.handleBlur}
            onUpdate={this.updateToggleableLocation}
            onError={this.handleError}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      case LocationUnknown.US_CITY_STATE_ZIP_INTERNATIONAL_CITY:
        return (
          <ToggleableLocation
            {...this.props}
            domesticFields={['city', 'stateZipcode']}
            internationalFields={['city', 'country']}
            onBlur={this.handleBlur}
            onUpdate={this.updateToggleableLocation}
            onError={this.handleError}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
          />
        );

      case LocationUnknown.ADDRESS:
      case LocationUnknown.US_ADDRESS:
        if (this.props.forceAPO) {
          this.enforceLayout(['state', 'city', 'zipcode', 'country', 'street']);
        }
        return (
          <AddressUnknown
            {...this.props}
            inUsaUnknown={this.props.inUsaUnknown}
            inUsaUnknownLabel={this.props.inUsaUnknownLabel}
            addressUnknown={this.props.addressUnknown}
            addressUnknownLabel={this.props.addressUnknownLabel}
            hideInstallationNameIfInternational={this.props.hideInstallationNameIfInternational}
            disabled={this.props.disabled}
            hideInstallationNameUnknownExplanation={this.props.hideInstallationNameUnknownExplanation}
            onFocus={this.handleFocus}
            onUpdateAddressUnknown={this.props.onUpdateAddressUnknown}
            showAddressUnknown={this.props.showAddressUnknown}
            onUpdateInUsaUnknown={this.props.onUpdateInUsaUnknown}
            showInUsaUnknown={this.props.showInUsaUnknown}
            onBlur={this.handleBlur}
            showInstallationNameUnknown={this.props.showInstallationNameUnknown}
            hideInstallationNameUnknownWhenForeign={this.props.hideInstallationNameUnknownWhenForeign}
            isEnabled={this.props.isEnabled}
            isPoBoxAllowed={this.props.isPoBoxAllowed}
            disableToggle={this.props.layout === LocationUnknown.US_ADDRESS || this.props.disableToggle}
            showMilitaryInstallation={this.props.showMilitaryInstallation}
            onUpdate={this.updateAddress}
            onError={this.handleError}
            required={this.props.required}
            useChangeWarning={this.props.useChangeWarning}
            showDisabledText={this.props.showDisabledText}
            showIsInstallationUnknownIfInternational={this.props.showIsInstallationUnknownIfInternational}
            showIsInstallationUnknownIfDomestic={this.props.showIsInstallationUnknownIfDomestic}
            showApoFpoUnknown={this.props.showApoFpoUnknown}
            showApoFpoDpoPostcodeWhenForeign={this.props.showApoFpoDpoPostcodeWhenForeign}
            showInternationalStreet={this.props.showInternationalStreet}
            showInternationalStreetUnknown={this.props.showInternationalStreetUnknown}
            internationalStreetLabel={this.props.internationalStreetLabel}
            includeMilitaryStates={this.props.includeMilitaryStates}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      case LocationUnknown.STATE:
        this.enforceLayout(['state']);
        return this.renderFields(['state']);
      case LocationUnknown.CITY_STATE:
        this.enforceLayout(['city', 'state']);
        return this.renderFields(['city', 'state']);
      case LocationUnknown.STREET_CITY_COUNTRY:
        this.enforceLayout(['street', 'city', 'country']);
        return this.renderFields(['street', 'city', 'country']);
      case LocationUnknown.CITY_COUNTRY:
        this.enforceLayout(['city', 'country']);
        return this.renderFields(['city', 'country']);
      case LocationUnknown.CITY_STATE_COUNTRY:
        return this.renderFields(['city', 'state', 'country']);
      case LocationUnknown.STREET_CITY:
        return this.renderFields(['street', 'city']);
      case LocationUnknown.COUNTRY:
        return this.renderFields(['country']);
      case LocationUnknown.OFFENSE:
        this.enforceLayout(['city', 'state', 'zipcode', 'county', 'country']);
        return (
          <ToggleableLocation
            {...this.props}
            country={this.props.country || { value: 'United States' }}
            domesticFields={['city', 'stateZipcode', 'county']}
            internationalFields={['city', 'country']}
            onBlur={this.handleBlur}
            onUpdate={this.updateToggleableLocation}
            onError={this.handleError}
            required={this.props.required}
            updateCountryFocused={this.updateCountryFocused}
          />
        );
      case null:
      case undefined:
      default:
        if (!env.IsTest()) {
          console.warn('Location layout not specified. Add one please');
        }
        return null;
    }
  }

  render() {
    const classNames = classnames('location', this.props.className);
    const show = this.state.suggestions && this.showSuggestions();
    const suggestions = this.state.geocodeResult.Suggestions || [];

    return (
      <div className={classNames}>
        <div className="fields">
          {this.state.spinner && (
            <Spinner
              show={this.state.spinner}
              action={this.state.spinnerAction}
              label={i18n.t('address.spinner')}
            />
          )}

          <Suggestions
            show={show}
            suggestions={suggestions}
            renderSuggestion={this.renderSuggestion} // eslint-disable-line @typescript-eslint/unbound-method
            suggestionTitle={this.suggestionTitle()}
            suggestionLabel={this.suggestionLabel()}
            suggestionParagraph={this.suggestionParagraph()}
            suggestionDismissLabel={i18n.t('suggestions.address.dismiss')}
            suggestionDismissContent={this.suggestionDismissContent()}
            suggestionDismissAlternate={this.dismissAlternative()}
            suggestionUseLabel={i18n.t('suggestions.address.use')}
            onSuggestion={this.onSuggestion}
            onDismiss={this.onSuggestionDismiss}
          />

          {this.fieldMappings()}
        </div>
      </div>
    );
  }
}

// Define all possible layouts for location fields
LocationUnknown.BIRTHPLACE = Layouts.BIRTHPLACE;
LocationUnknown.IDENTIFICATION_BIRTH_PLACE = Layouts.IDENTIFICATION_BIRTH_PLACE;
LocationUnknown.PVQ_COURT_MARTIAL_LOCATION = Layouts.PVQ_COURT_MARTIAL_LOCATION;
LocationUnknown.PVQ_COURT_MARTIAL_OFFENSE_LOCATION = Layouts.PVQ_COURT_MARTIAL_OFFENSE_LOCATION;
LocationUnknown.PVQ_DOMESTIC_FOREIGN_MILITARY_DIPLOMATIC_LOCATION = Layouts.PVQ_DOMESTIC_FOREIGN_MILITARY_DIPLOMATIC_LOCATION;
LocationUnknown.COUNTRY = Layouts.COUNTRY;
LocationUnknown.US_CITY_STATE_INTERNATIONAL_CITY_COUNTRY = Layouts.US_CITY_STATE_INTERNATIONAL_CITY_COUNTRY;
LocationUnknown.US_STREET_CITY_STATE_ZIP = Layouts.US_STREET_CITY_STATE_ZIP;
LocationUnknown.US_CITY_STATE_COUNTY_INTERNATIONAL_CITY_COUNTRY = Layouts.US_CITY_STATE_COUNTY_INTERNATIONAL_CITY_COUNTRY;
LocationUnknown.US_COUNTY_CITY_STATE_ZIP_INTERNATIONAL_CITY_COUNTRY = Layouts.US_COUNTY_CITY_STATE_ZIP_INTERNATIONAL_CITY_COUNTRY;
LocationUnknown.BIRTHPLACE_WITHOUT_COUNTY = Layouts.BIRTHPLACE_WITHOUT_COUNTY;
LocationUnknown.INTERNATIONAL_CITY_COUNTRY_ONLY = Layouts.INTERNATIONAL_CITY_COUNTRY_ONLY;
LocationUnknown.STATE = Layouts.STATE;
LocationUnknown.CITY_STATE = Layouts.CITY_STATE;
LocationUnknown.STREET_CITY_COUNTRY = Layouts.STREET_CITY_COUNTRY;
LocationUnknown.CITY_COUNTRY = Layouts.CITY_COUNTRY;
LocationUnknown.US_CITY_STATE_ZIP_INTERNATIONAL_CITY = Layouts.US_CITY_STATE_ZIP_INTERNATIONAL_CITY;
LocationUnknown.ADDRESS = Layouts.ADDRESS;
LocationUnknown.CITY_STATE_COUNTRY = Layouts.CITY_STATE_COUNTRY;
LocationUnknown.US_ADDRESS = Layouts.US_ADDRESS;
LocationUnknown.STREET_CITY = Layouts.STREET_CITY;
LocationUnknown.OFFENSE = Layouts.OFFENSE;

LocationUnknown.defaultProps = {
  addressUnknown: { applicable: true },
  addressUnknownLabel: i18n.t('address.pvq.idkCapitalized'),
  inUsaUnknown: { applicable: true },
  inUsaUnknownLabel: i18n.t('address.pvq.idkCapitalized'),
  disabled: false,
  domesticTitle: '',
  foreignTitle: '',
  hideInstallationNameIfInternational: false,
  hideInstallationNameUnknownExplanation: false,
  militaryDiplomaticInstallationNameLabel: i18n.t('address.pvq.installation.name.title'),
  name: 'location',
  onUpdateAddressUnknown: (addressUnknown: ApplicableModel) => { },
  showAddressUnknown: false,
  onUpdateInUsaUnknown: (inUsaUnknown: ApplicableModel) => { },
  showInUsaUnknown: false,
  showIsInstallationUnknownIfInternational: false,
  showIsInstallationUnknownIfDomestic: false,
  showInstallationNameUnknown: false,
  hideInstallationNameUnknownWhenForeign: false,
  showApoFpoDpoPostcodeWhenForeign: false,
  showApoFpoUnknown: false,
  showInternationalStreet: false,
  showInternationalStreetUnknown: false,
  layout: Layouts.ADDRESS,
  street: '',
  validated: false,
  geocode: false,
  geocodeResult: {},
  spinner: false,
  suggestions: false,
  countryFocused: false,
  streetLabel: i18n.t('address.pvq.us.street.label'),
  internationalStreetLabel: i18n.t('address.pvq.us.street.label'),
  street2Label: i18n.t('address.pvq.us.street2.label'),
  stateLabel: i18n.t('address.pvq.us.state.label'),
  cityLabel: i18n.t('address.pvq.us.city.label'),
  zipcodeLabel: i18n.t('address.pvq.us.zipcode.label'),
  countyLabel: i18n.t('address.pvq.us.county.label'),
  countryLabel: i18n.t('address.pvq.international.country.label'),
  required: false,
  disableToggle: false,
  addressBooks: {},
  addressBook: '',
  isPoBoxAllowed: true,
  includeMilitaryStates: false,
  apoFpoDpoPostcode: '',
  apoFpoNotApplicable: { applicable: true },
  // TODO: Should this be {} instead of ''?
  isMilitaryDiplomaticAddress: '',
  militaryDiplomaticInstallationName: '',
  // TODO: Should this be {} instead of ''?
  militaryInstallationNameNotApplicable: { applicable: true },
  militaryDiplomaticInstallationNameExplanation: '',
  excludeUnitedStates: true,
  showPostOffice: false,
  showMilitaryInstallation: false,
  showDisabledText: false,
  onUpdate: () => { },
  dispatch: () => { },
  onError: (value, arr) => arr,
  useChangeWarning: false,
};

LocationUnknown.errors = [];

const mapStateToProps = (state) => ({
  form: state.form
});

const mapDispatchToProps = (dispatch) => ({
  updateCurrentScrollValue: (value) => dispatch(updateCurrentScrollValue(value))
});

export default connect(mapStateToProps, mapDispatchToProps)(LocationUnknown);
